<script lang="ts" setup generic="UNUSED">
import type { HTMLAttributes } from 'vue';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

const button = cva('border-radius focus:ring-blue relative m-0 inline-flex appearance-none items-center justify-center text-sm font-medium leading-5 transition duration-150 ease-in-out hover:no-underline focus:outline-offset-2 active:scale-[0.98]', {
  variants: {
    variant: {
      primary: 'bg-dark-green fill-white text-white hover:bg-secondary',
      secondary: 'bg-white fill-dark-green text-dark-green shadow-[inset_0_0_0_2px_#4d7d34] hover:bg-dark-green hover:fill-white hover:text-white',
      warning: 'bg-warning fill-white text-white hover:bg-[#db8c0a]',
      danger: 'fill-danger text-danger shadow-[inset_0_0_0_2px_#f85359] hover:bg-danger hover:fill-white hover:text-white',
      text: 'text-dark-green hover:underline active:scale-100',
    },
    size: {
      default: 'w-auto max-w-full rounded-[2.344rem] px-4 py-2.5',
      icon: 'size-[50px] rounded-full p-0',
      small: 'p-0',
    },
    disabled: {
      true: 'hover:bg-currentColor hover:text-currentColor hover:fill-curentColor cursor-not-allowed opacity-40 active:scale-100', // bg-gray-40
    },
  },
});

type ButtonVariantProps = VariantProps<typeof button>;
type ButtonProps = {
  tag: 'button';
  type?: 'button' | 'submit' | 'reset';
  href?: never;
  disabled?: boolean;
  size?: ButtonVariantProps['size'];
  variant?: ButtonVariantProps['variant'];
  class?: HTMLAttributes['class'];
} | {
  tag: 'NuxtLink';
  href: string;
  type?: never;
  disabled?: never;
  size?: ButtonVariantProps['size'];
  variant?: ButtonVariantProps['variant'];
  class?: HTMLAttributes['class'];
} | {
  tag: 'div';
  href?: never;
  type?: never;
  disabled?: never;
  size?: ButtonVariantProps['size'];
  variant?: ButtonVariantProps['variant'];
  class?: HTMLAttributes['class'];
};

const props = withDefaults(defineProps<ButtonProps>(), {
  type: 'button',
  disabled: false,
  size: 'default',
  variant: 'primary',
});

const component = computed(() => {
  if (props.tag === 'NuxtLink') {
    return resolveComponent('NuxtLink');
  }
  return props.tag;
});

const componentProps = computed(() => {
  if (props.tag === 'button') {
    return {
      type: props.type,
      class: twMerge(button({ variant: props.variant, disabled: props.disabled, size: props.size }), props.class),
      disabled: props.disabled,
    };
  } else if (props.tag === 'NuxtLink') {
    return {
      to: props.href,
      class: twMerge(button({ variant: props.variant, size: props.size }), props.class),
    };
  } else {
    return {
      class: twMerge(button({ variant: props.variant, size: props.size }), props.class),
    };
  }
});
</script>

<template>
  <component :is="component" v-bind="componentProps">
    <slot />
  </component>
</template>

<style>

</style>
